import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Play,
  Pause,
  Trash2,
  Edit2,
  Copy,
  BarChart3,
  MessageCircle,
  Clock,
  GitBranch,
  ShoppingCart,
  UserPlus,
  Truck,
  CreditCard,
  Users,
  ArrowRight,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Save,
  Eye,
  Loader2,
  Zap,
  Mail,
  Bell,
  Filter,
  Package,
  AlertCircle,
  CheckCircle,
  XCircle,
  TrendingUp,
  DollarSign,
  MousePointer,
  Send,
  RefreshCw,
  Settings,
  LogOut,
  LogIn,
  Hash,
  FileText,
  Info,
  Pencil,
  Image,
  Link,
  MessageSquare,
  Database,
  MapPin,
  AlertTriangle,
  RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// =============================================================================
// CONFIGURATION
// =============================================================================

// Entry Trigger types
const ENTRY_TRIGGERS = [
  { value: 'order_created', label: 'Order Placed', icon: ShoppingCart, color: 'bg-blue-500', category: 'Orders' },
  { value: 'checkout_started', label: 'Checkout Started', icon: ShoppingCart, color: 'bg-blue-400', category: 'Orders' },
  { value: 'checkout_abandoned', label: 'Checkout Abandoned', icon: ShoppingCart, color: 'bg-orange-500', category: 'Orders' },
  { value: 'customer_created', label: 'Customer Created', icon: UserPlus, color: 'bg-green-500', category: 'Customers' },
  { value: 'order_fulfilled', label: 'Order Fulfilled', icon: Package, color: 'bg-purple-500', category: 'Fulfillment' },
  { value: 'order_shipped', label: 'Order Shipped', icon: Truck, color: 'bg-indigo-500', category: 'Fulfillment' },
  { value: 'order_delivered', label: 'Order Delivered', icon: CheckCircle, color: 'bg-green-600', category: 'Fulfillment' },
  { value: 'awb_generated', label: 'AWB Generated', icon: Truck, color: 'bg-cyan-500', category: 'Fulfillment' },
  { value: 'courier_failed', label: 'Failed Delivery Attempt (NDR)', icon: AlertTriangle, color: 'bg-orange-500', category: 'Fulfillment' },
  { value: 'order_delivery_rescheduled', label: 'Delivery Rescheduled', icon: Clock, color: 'bg-amber-600', category: 'Fulfillment' },
  { value: 'return_accepted', label: 'Return Accepted', icon: RotateCcw, color: 'bg-teal-600', category: 'Returns' },
  { value: 'return_picked_up', label: 'Return Picked Up', icon: Package, color: 'bg-teal-500', category: 'Returns' },
  { value: 'return_qc_completed', label: 'Return QC Completed', icon: CheckCircle, color: 'bg-emerald-600', category: 'Returns' },
  { value: 'return_refund_issued', label: 'Refund Issued', icon: DollarSign, color: 'bg-green-600', category: 'Returns' },
  { value: 'courier_rto', label: 'RTO (Return to Origin)', icon: RotateCcw, color: 'bg-red-500', category: 'Fulfillment' },
  { value: 'order_cancelled', label: 'Order Cancelled', icon: XCircle, color: 'bg-red-500', category: 'Orders' },
  { value: 'order_refunded', label: 'Order Refunded', icon: DollarSign, color: 'bg-red-400', category: 'Orders' },
  { value: 'payment_received', label: 'Payment Received', icon: CreditCard, color: 'bg-yellow-500', category: 'Payments' },
  { value: 'payment_failed', label: 'Payment Failed', icon: CreditCard, color: 'bg-red-500', category: 'Payments' },
  { value: 'lead_created', label: 'Lead Created', icon: Users, color: 'bg-pink-500', category: 'Leads' },
  // Store Credits Triggers
  { value: 'credits_added', label: 'Store Credits Added', icon: DollarSign, color: 'bg-emerald-500', category: 'Store Credits' },
  { value: 'credits_expiring', label: 'Credits Expiring Soon', icon: Clock, color: 'bg-amber-500', category: 'Store Credits' },
  { value: 'credits_expired', label: 'Credits Expired', icon: XCircle, color: 'bg-red-400', category: 'Store Credits' },
  // Production (made-to-order) Triggers
  { value: 'production_started', label: 'Production Started', icon: Package, color: 'bg-amber-500', category: 'Production' },
  { value: 'production_eta_updated', label: 'Production ETA Updated', icon: Clock, color: 'bg-amber-400', category: 'Production' },
  { value: 'production_completed', label: 'Production Completed', icon: CheckCircle, color: 'bg-emerald-600', category: 'Production' },
  { value: 'manual', label: 'Manual Trigger', icon: Zap, color: 'bg-gray-500', category: 'Other' }
];

// Exit Triggers
const EXIT_TRIGGERS = [
  { value: 'order_placed', label: 'Order Placed', icon: ShoppingCart, category: 'Orders' },
  { value: 'checkout_completed', label: 'Checkout Completed', icon: CheckCircle, category: 'Orders' },
  { value: 'order_shipped', label: 'Order Shipped', icon: Truck, category: 'Fulfillment' },
  { value: 'order_delivered', label: 'Order Delivered', icon: CheckCircle, category: 'Fulfillment' },
  { value: 'order_cancelled', label: 'Order Cancelled', icon: XCircle, category: 'Orders' },
  { value: 'unsubscribed', label: 'User Unsubscribed', icon: LogOut, category: 'Engagement' },
  { value: 'replied', label: 'Customer Replied', icon: MessageCircle, category: 'Engagement' },
  { value: 'custom_date', label: 'Custom Date Passed', icon: Clock, category: 'Time' },
  // Store Credits Exit Triggers
  { value: 'credits_used', label: 'Credits Used', icon: DollarSign, category: 'Store Credits' },
  { value: 'credits_expired', label: 'Credits Expired', icon: XCircle, category: 'Store Credits' }
];

// Action types for nodes
const ACTION_TYPES = {
  send_whatsapp: { label: 'Send WhatsApp', icon: MessageCircle, color: 'bg-green-500' },
  send_email: { label: 'Send Email', icon: Mail, color: 'bg-blue-500', disabled: true, badge: 'Coming Soon' },
  send_sms: { label: 'Send SMS', icon: Bell, color: 'bg-purple-500', disabled: true, badge: 'Coming Soon' },
  delay: { label: 'Wait For / Till Time', icon: Clock, color: 'bg-orange-500' },
  wait_for_response: { label: 'Wait for Response', icon: MessageSquare, color: 'bg-teal-500' },
  update_data: { label: 'Update Data', icon: Database, color: 'bg-indigo-500' },
  condition: { label: 'Condition', icon: GitBranch, color: 'bg-cyan-500' },
  split: { label: 'A/B Split', icon: GitBranch, color: 'bg-pink-500' },
  end: { label: 'End Journey', icon: X, color: 'bg-gray-500' }
};

// Variable fields available for mapping
const VARIABLE_FIELDS = {
  customer: [
    { value: 'customer.first_name', label: 'Customer First Name' },
    { value: 'customer.last_name', label: 'Customer Last Name' },
    { value: 'customer.full_name', label: 'Customer Full Name' },
    { value: 'customer.email', label: 'Customer Email' },
    { value: 'customer.phone', label: 'Customer Phone' },
    { value: 'customer.city', label: 'Customer City' },
    { value: 'customer.country', label: 'Customer Country' },
    { value: 'customer.total_spent', label: 'Customer Total Spent' },
    { value: 'customer.orders_count', label: 'Customer Orders Count' },
  ],
  order: [
    { value: 'order.number', label: 'Order Number' },
    { value: 'order.total', label: 'Order Total' },
    { value: 'order.subtotal', label: 'Order Subtotal' },
    { value: 'order.currency', label: 'Order Currency' },
    { value: 'order.items_count', label: 'Items Count' },
    { value: 'order.status', label: 'Order Status' },
    { value: 'order.payment_status', label: 'Payment Status' },
    { value: 'order.created_date', label: 'Order Date' },
    { value: 'order.discount_amount', label: 'Discount Amount' },
    { value: 'order.shipping_amount', label: 'Shipping Amount' },
  ],
  production: [
    { value: 'order.production_expected_date', label: 'Expected Ready Date (e.g. 14 Oct 2026)' },
    { value: 'order.production_expected_date_raw', label: 'Expected Ready Date (YYYY-MM-DD)' },
    { value: 'order.production_notes', label: 'Production Notes' },
  ],
  delivery_estimate: [
    { value: 'order.delivery_estimate', label: 'Estimated Delivery Range (e.g. 26 Sep – 27 Sep)' },
    { value: 'order.delivery_estimate_min', label: 'Estimated Delivery (earliest)' },
    { value: 'order.delivery_estimate_max', label: 'Estimated Delivery (latest)' },
  ],
  reschedule: [
    { value: 'order.reschedule_reason', label: 'Reschedule Reason (e.g. Customer not available)' },
    { value: 'order.rescheduled_delivery_date', label: 'New Delivery Date (e.g. 14 Oct 2026)' },
    { value: 'order.rescheduled_delivery_date_raw', label: 'New Delivery Date (YYYY-MM-DD)' },
    { value: 'order.delivery_attempt_count', label: 'Delivery Attempt Number' },
  ],
  returns: [
    { value: 'order.return_number', label: 'Return Number' },
    { value: 'order.return_status', label: 'Return Status' },
    { value: 'order.refund_amount', label: 'Refund Amount' },
  ],
  fulfillment: [
    { value: 'fulfillment.status', label: 'Fulfillment Status' },
    { value: 'fulfillment.tracking_number', label: 'Tracking Number' },
    { value: 'fulfillment.carrier', label: 'Carrier Name' },
    { value: 'fulfillment.tracking_url', label: 'Tracking URL' },
    { value: 'fulfillment.shipped_date', label: 'Shipped Date' },
    { value: 'fulfillment.delivered_date', label: 'Delivered Date' },
  ],
  awb: [
    // Surfaced on the "AWB Generated" trigger — cleaner variable names
    // than `fulfillment.tracking_number` so WhatsApp templates read
    // naturally ("Your AWB is {{1}}, dispatched via {{2}}").
    { value: 'awb.number', label: 'AWB Number' },
    { value: 'awb.courier_name', label: 'Courier Name' },
    { value: 'awb.tracking_url', label: 'Courier Tracking URL' },
    { value: 'awb.tracking_link', label: 'Opswarex Tracking Link' },
  ],
  product: [
    { value: 'product.first_item_name', label: 'First Item Name' },
    { value: 'product.first_item_price', label: 'First Item Price' },
    { value: 'product.first_item_image', label: 'First Item Image URL' },
    { value: 'product.first_item_variant_image', label: 'First Item Variant Image URL' },
    { value: 'product.all_items', label: 'All Items List' },
  ],
  checkout: [
    { value: 'checkout.total_price', label: 'Cart Total' },
    { value: 'checkout.items_count', label: 'Cart Items Count' },
    { value: 'checkout.recovery_url', label: 'Checkout Recovery URL' },
    { value: 'checkout.first_item_name', label: 'First Cart Item Name' },
    { value: 'checkout.first_item_image', label: 'First Cart Item Image URL' },
    { value: 'checkout.currency', label: 'Cart Currency' },
  ],
  store_credits: [
    { value: 'store_credits.credits_added', label: 'Credits Added Amount' },
    { value: 'store_credits.credits_expiring', label: 'Credits Expiring Soon' },
    { value: 'store_credits.credits_expired', label: 'Credits Expired' },
    { value: 'store_credits.credits_removed', label: 'Credits Removed' },
    { value: 'store_credits.total_balance', label: 'Total Credit Balance' },
    { value: 'store_credits.currency', label: 'Credit Currency' },
    { value: 'store_credits.expiry_date', label: 'Credit Expiry Date' },
    { value: 'store_credits.days_until_expiry', label: 'Days Until Expiry' },
  ],
  shop: [
    { value: 'shop.name', label: 'Shop Name' },
    { value: 'shop.url', label: 'Shop URL' },
    { value: 'shop.support_email', label: 'Support Email' },
    { value: 'shop.support_phone', label: 'Support Phone' },
  ],
  response: [
    { value: 'response.received', label: 'Response Received (true/false)' },
    { value: 'response.type', label: 'Response Type (text/location/image)' },
    { value: 'response.lat', label: 'Location Latitude' },
    { value: 'response.lng', label: 'Location Longitude' },
    { value: 'response.location_name', label: 'Location Name' },
    { value: 'response.location_address', label: 'Location Address' },
    { value: 'response.text', label: 'Text Content' },
    { value: 'response.source', label: 'Response Source (whatsapp)' },
  ]
};

// Header media source options for templates with media headers.
// Dynamic product/cart sources are only shown for triggers that
// actually carry product data — customer/lead/credit triggers don't,
// so surfacing those options there would guarantee a runtime failure.
const HEADER_MEDIA_SOURCES_ALL = [
  { value: 'static', label: 'Static URL', description: 'Fixed image/video URL', requires: 'any' },
  { value: 'product.first_item_image', label: 'Product Image', description: 'First product image from order', requires: 'product' },
  { value: 'product.first_item_variant_image', label: 'Variant Image', description: 'First variant image from order', requires: 'product' },
  { value: 'checkout.first_item_image', label: 'Cart Item Image', description: 'First item image from abandoned cart', requires: 'checkout' },
  { value: 'custom', label: 'Custom Field', description: 'Specify custom data field', requires: 'any' },
];

// Triggers that produce order/line-item data (have product images on the
// event payload or resolvable via inventory SKU lookup).
const TRIGGERS_WITH_PRODUCT = new Set([
  'order_created', 'order_fulfilled', 'order_shipped', 'order_delivered',
  'order_cancelled', 'order_refunded', 'payment_received', 'payment_failed',
  'awb_generated', 'courier_assigned', 'courier_picked_up',
  'courier_in_transit', 'courier_delivered', 'courier_failed', 'courier_rto',
  'order_ready_for_dispatch', 'order_out_for_delivery', 'order_delivery_failed',
  'order_delivery_rescheduled',
]);

// Triggers that produce cart/checkout data.
const TRIGGERS_WITH_CHECKOUT = new Set([
  'checkout_started', 'checkout_abandoned',
]);

/**
 * Return only the header-media sources that make sense for the given
 * trigger. Triggers like `customer_created` or `lead_created` have no
 * product/cart in their payload, so we only offer Static URL + Custom
 * Field options — preventing users from shipping a journey that's
 * doomed to fail at send-time with "no image URL available".
 */
function headerMediaSourcesForTrigger(triggerType) {
  const hasProduct = TRIGGERS_WITH_PRODUCT.has(triggerType);
  const hasCheckout = TRIGGERS_WITH_CHECKOUT.has(triggerType);
  return HEADER_MEDIA_SOURCES_ALL.filter((s) => {
    if (s.requires === 'any') return true;
    if (s.requires === 'product') return hasProduct;
    if (s.requires === 'checkout') return hasCheckout;
    return false;
  });
}

// Back-compat export — some older code paths still import the flat array.
const HEADER_MEDIA_SOURCES = HEADER_MEDIA_SOURCES_ALL;

// =============================================================================
// JOURNEY ANALYTICS DIALOG
// =============================================================================

const JourneyAnalyticsDialog = ({ open, onClose, journey }) => {
  const { formatCurrency } = useSettings();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('30');
  
  useEffect(() => {
    if (open && journey?.id) {
      loadAnalytics();
    }
  }, [open, journey?.id, dateRange]);
  
  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/v1/journeys/${journey.id}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load journey analytics');
    } finally {
      setLoading(false);
    }
  };
  
  const getNodeIcon = (type) => {
    switch (type) {
      case 'send_whatsapp': return <MessageCircle className="h-4 w-4 text-green-400" />;
      case 'send_email': return <Mail className="h-4 w-4 text-blue-400" />;
      case 'delay': return <Clock className="h-4 w-4 text-yellow-400" />;
      case 'condition': return <GitBranch className="h-4 w-4 text-purple-400" />;
      case 'trigger': return <Zap className="h-4 w-4 text-wt-gold" />;
      default: return <CheckCircle className="h-4 w-4 text-gray-400" />;
    }
  };
  
  const getNodeTypeName = (type) => {
    switch (type) {
      case 'send_whatsapp': return 'WhatsApp Message';
      case 'send_email': return 'Email';
      case 'delay': return 'Wait/Delay';
      case 'condition': return 'Condition';
      case 'trigger': return 'Trigger';
      case 'end': return 'End';
      default: return type;
    }
  };
  
  // Build node flow from journey
  const getOrderedNodes = () => {
    if (!journey?.nodes) return [];
    
    // Start with nodes that have no incoming connections
    const nodes = [...journey.nodes];
    const orderedNodes = [];
    const visited = new Set();
    
    // Simple ordering by position or array order
    return nodes.map((node, idx) => ({
      ...node,
      order: idx + 1,
      stats: analytics?.node_stats?.[node.id] || {}
    }));
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-wt-surface border border-wt-border text-wt-text-primary max-w-4xl max-h-[90vh] overflow-hidden" data-testid="journey-analytics-dialog">
        <DialogHeader>
          <DialogTitle className="text-wt-gold flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Journey Analytics
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
          </div>
        ) : analytics ? (
          <div className="space-y-6 overflow-y-auto max-h-[70vh] pr-2">
            {/* Journey Info Header */}
            <div className="bg-wt-subtle p-4 rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-wt-text-primary">{journey?.name}</h3>
                  <p className="text-sm text-wt-text-muted mt-1">
                    Trigger: {ENTRY_TRIGGERS.find(t => t.value === journey?.trigger_type)?.label || journey?.trigger_type}
                  </p>
                </div>
                <Badge className={journey?.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                  {journey?.status}
                </Badge>
              </div>
            </div>
            
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-wt-paper border border-wt-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="h-4 w-4 text-blue-400" />
                  <span className="text-xs text-wt-text-muted">Entered</span>
                </div>
                <p className="text-xl font-bold text-wt-text-primary">{analytics.totals?.entered || 0}</p>
              </div>
              <div className="bg-wt-paper border border-wt-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="h-4 w-4 text-green-400" />
                  <span className="text-xs text-wt-text-muted">Open Rate</span>
                </div>
                <p className="text-xl font-bold text-green-400">{analytics.open_rate || 0}%</p>
              </div>
              <div className="bg-wt-paper border border-wt-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <MousePointer className="h-4 w-4 text-blue-400" />
                  <span className="text-xs text-wt-text-muted">Click Rate</span>
                </div>
                <p className="text-xl font-bold text-blue-400">{analytics.click_rate || 0}%</p>
              </div>
              <div className="bg-wt-paper border border-wt-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="h-4 w-4 text-purple-400" />
                  <span className="text-xs text-wt-text-muted">Conversion</span>
                </div>
                <p className="text-xl font-bold text-purple-400">{analytics.conversion_rate || 0}%</p>
              </div>
              <div className="bg-wt-paper border border-wt-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="h-4 w-4 text-wt-gold" />
                  <span className="text-xs text-wt-text-muted">Revenue</span>
                </div>
                <p className="text-xl font-bold text-wt-gold">{formatCurrency(analytics.revenue || 0)}</p>
              </div>
            </div>
            
            {/* Journey Funnel */}
            <div className="bg-wt-paper border border-wt-border rounded-lg p-4">
              <h4 className="text-sm font-medium text-wt-text-primary mb-3 flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-wt-gold" />
                Journey Funnel
              </h4>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 text-center">
                  <p className="text-2xl font-bold text-blue-400">{analytics.totals?.entered || 0}</p>
                  <p className="text-xs text-wt-text-muted">Entered</p>
                </div>
                <ArrowRight className="h-5 w-5 text-wt-text-muted" />
                <div className="flex-1 text-center">
                  <p className="text-2xl font-bold text-yellow-400">{analytics.totals?.in_progress || 0}</p>
                  <p className="text-xs text-wt-text-muted">In Progress</p>
                </div>
                <ArrowRight className="h-5 w-5 text-wt-text-muted" />
                <div className="flex-1 text-center">
                  <p className="text-2xl font-bold text-green-400">{analytics.totals?.completed || 0}</p>
                  <p className="text-xs text-wt-text-muted">Completed</p>
                </div>
                <ArrowRight className="h-5 w-5 text-wt-text-muted" />
                <div className="flex-1 text-center">
                  <p className="text-2xl font-bold text-orange-400">{analytics.totals?.exited || 0}</p>
                  <p className="text-xs text-wt-text-muted">Exited Early</p>
                </div>
                <ArrowRight className="h-5 w-5 text-wt-text-muted" />
                <div className="flex-1 text-center">
                  <p className="text-2xl font-bold text-red-400">{analytics.totals?.failed || 0}</p>
                  <p className="text-xs text-wt-text-muted">Failed</p>
                </div>
              </div>
            </div>
            
            {/* WhatsApp Stats */}
            {analytics.whatsapp && (
              <div className="bg-wt-paper border border-wt-border rounded-lg p-4">
                <h4 className="text-sm font-medium text-wt-text-primary mb-3 flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-green-400" />
                  WhatsApp Message Stats
                </h4>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-wt-subtle rounded-lg">
                    <Send className="h-5 w-5 text-blue-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-wt-text-primary">{analytics.whatsapp.sent || 0}</p>
                    <p className="text-xs text-wt-text-muted">Sent</p>
                  </div>
                  <div className="text-center p-3 bg-wt-subtle rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-wt-text-primary">{analytics.whatsapp.delivered || 0}</p>
                    <p className="text-xs text-wt-text-muted">Delivered</p>
                  </div>
                  <div className="text-center p-3 bg-wt-subtle rounded-lg">
                    <Eye className="h-5 w-5 text-purple-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-wt-text-primary">{analytics.whatsapp.read || 0}</p>
                    <p className="text-xs text-wt-text-muted">Read</p>
                  </div>
                  <div className="text-center p-3 bg-wt-subtle rounded-lg">
                    <XCircle className="h-5 w-5 text-red-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-wt-text-primary">{analytics.whatsapp.failed || 0}</p>
                    <p className="text-xs text-wt-text-muted">Failed</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step-by-Step Analytics */}
            <div className="bg-wt-paper border border-wt-border rounded-lg p-4">
              <h4 className="text-sm font-medium text-wt-text-primary mb-4 flex items-center gap-2">
                <ArrowDown className="h-4 w-4 text-wt-gold" />
                Step-by-Step Performance
              </h4>
              
              {getOrderedNodes().length > 0 ? (
                <div className="space-y-3">
                  {getOrderedNodes().map((node, idx) => {
                    const stats = node.stats || {};
                    const isMessageNode = node.type === 'send_whatsapp' || node.type === 'send_email';
                    
                    return (
                      <div key={node.id} className="relative">
                        {/* Connector line */}
                        {idx > 0 && (
                          <div className="absolute left-6 -top-3 w-0.5 h-3 bg-wt-border" />
                        )}
                        
                        <div className="flex items-start gap-3 p-3 bg-wt-subtle rounded-lg hover:bg-wt-subtle/80 transition-colors">
                          {/* Step number and icon */}
                          <div className="flex items-center gap-2 min-w-[100px]">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-wt-bg border border-wt-border">
                              <span className="text-xs font-bold text-wt-text-muted">{node.order}</span>
                            </div>
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-wt-bg">
                              {getNodeIcon(node.type)}
                            </div>
                          </div>
                          
                          {/* Node info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-wt-text-primary text-sm">
                                {getNodeTypeName(node.type)}
                              </span>
                              {node.template_name && (
                                <Badge variant="outline" className="text-xs border-green-500/30 text-green-400">
                                  {node.template_name}
                                </Badge>
                              )}
                              {node.delay_duration && (
                                <Badge variant="outline" className="text-xs border-yellow-500/30 text-yellow-400">
                                  Wait {node.delay_duration} {node.delay_unit || 'hours'}
                                </Badge>
                              )}
                            </div>
                            {node.config?.template_name && (
                              <p className="text-xs text-wt-text-muted mt-0.5">Template: {node.config.template_name}</p>
                            )}
                          </div>
                          
                          {/* Node stats */}
                          {isMessageNode && (
                            <div className="flex items-center gap-4 text-right">
                              <div>
                                <p className="text-sm font-medium text-wt-text-primary">{stats.sent || 0}</p>
                                <p className="text-xs text-wt-text-muted">Sent</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-green-400">{stats.delivered || 0}</p>
                                <p className="text-xs text-wt-text-muted">Delivered</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-purple-400">{stats.read || 0}</p>
                                <p className="text-xs text-wt-text-muted">Read</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-blue-400">{stats.clicked || 0}</p>
                                <p className="text-xs text-wt-text-muted">Clicked</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-red-400">{stats.failed || 0}</p>
                                <p className="text-xs text-wt-text-muted">Failed</p>
                              </div>
                              {stats.revenue > 0 && (
                                <div>
                                  <p className="text-sm font-medium text-wt-gold">{formatCurrency(stats.revenue)}</p>
                                  <p className="text-xs text-wt-text-muted">Revenue</p>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {!isMessageNode && node.type !== 'trigger' && node.type !== 'end' && (
                            <div className="text-right min-w-[80px]">
                              <p className="text-sm text-wt-text-muted">—</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-wt-text-muted">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No steps configured in this journey</p>
                </div>
              )}
            </div>
            
            {/* Recent Failures - Only show if there are failures */}
            {analytics?.recent_failures?.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h4 className="text-sm font-medium text-red-400 mb-3 flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Recent Failures ({analytics.recent_failures.length})
                </h4>
                <div className="space-y-2">
                  {analytics.recent_failures.map((failure, idx) => (
                    <div key={idx} className="bg-wt-paper border border-wt-border rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-red-400 font-medium">{failure.error || 'Unknown error'}</p>
                          <p className="text-xs text-wt-text-muted mt-1">
                            Phone: {failure.phone || 'N/A'}
                          </p>
                        </div>
                        <p className="text-xs text-wt-text-muted whitespace-nowrap">
                          {failure.timestamp ? new Date(failure.timestamp).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-wt-text-muted">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No analytics data available</p>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-wt-text-secondary">
            Close
          </Button>
          <Button 
            onClick={loadAnalytics} 
            disabled={loading}
            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// JOURNEY LIST VIEW (Dashboard)
// =============================================================================

const JourneyListDashboard = ({ journeys, templates, onCreateJourney, onEditJourney, onDeleteJourney, onDuplicateJourney, onToggleStatus, onViewAnalytics, loading, formatCurrency }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Calculate metrics
  const metrics = useMemo(() => {
    const active = journeys.filter(j => j.status === 'active');
    return {
      totalTriggered: journeys.reduce((sum, j) => sum + (j.total_entered || 0), 0),
      totalRevenue: journeys.reduce((sum, j) => sum + (j.total_revenue || 0), 0),
      avgConversionRate: active.length > 0 
        ? (active.reduce((sum, j) => sum + (j.conversion_rate || 0), 0) / active.length).toFixed(2)
        : 0
    };
  }, [journeys]);
  
  const filteredJourneys = useMemo(() => {
    let result = journeys;
    if (activeTab !== 'all') {
      result = result.filter(j => j.status === activeTab);
    }
    if (searchQuery) {
      result = result.filter(j => j.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return result;
  }, [journeys, activeTab, searchQuery]);
  
  const counts = {
    all: journeys.length,
    active: journeys.filter(j => j.status === 'active').length,
    paused: journeys.filter(j => j.status === 'paused').length,
    draft: journeys.filter(j => j.status === 'draft').length
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Live</Badge>;
      case 'paused':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Paused</Badge>;
      case 'draft':
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Draft</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };
  
  const getTriggerInfo = (triggerType) => {
    return ENTRY_TRIGGERS.find(t => t.value === triggerType) || { label: triggerType, icon: Zap, color: 'bg-gray-500' };
  };
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-playfair text-wt-text-primary">Your Journeys</h1>
          <p className="text-wt-text-muted mt-1">Build automated customer journeys with WhatsApp messaging</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => window.dispatchEvent(new CustomEvent('openTemplateGallery'))}
            className="border-wt-border text-wt-text-muted hover:border-wt-gold hover:text-wt-gold"
            data-testid="use-template-btn"
          >
            <FileText className="h-4 w-4 mr-2" />
            Use Template
          </Button>
          <Button
            onClick={onCreateJourney}
            className="bg-wt-gold hover:bg-wt-gold-dark text-black"
            data-testid="create-journey-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create from Scratch
          </Button>
        </div>
      </div>
      
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Zap className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-wt-text-primary">{metrics.totalTriggered.toLocaleString()}</p>
                <p className="text-xs text-wt-text-muted">Unique Journeys Triggered</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <DollarSign className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-wt-text-primary">{formatCurrency(metrics.totalRevenue)}</p>
                <p className="text-xs text-wt-text-muted">Journey Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <TrendingUp className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-wt-text-primary">{metrics.avgConversionRate}%</p>
                <p className="text-xs text-wt-text-muted">Conversion Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs and Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full md:w-auto">
          <TabsList className="bg-wt-surface border border-wt-border">
            <TabsTrigger value="all" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
              All Journeys
            </TabsTrigger>
            <TabsTrigger value="active" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
              Live Journeys
            </TabsTrigger>
            <TabsTrigger value="draft" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
              Draft Journeys
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <Input
          placeholder="Search by journey name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-wt-paper border-wt-border text-wt-text-primary w-full md:w-64"
        />
      </div>
      
      {/* Journey Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
        </div>
      ) : filteredJourneys.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-wt-text-muted">
          <Zap className="h-16 w-16 mb-4 opacity-30" />
          <h3 className="text-lg font-medium text-wt-text-primary mb-2">No Journeys Yet</h3>
          <p className="text-sm mb-4">Create your first automated customer journey</p>
          <Button onClick={onCreateJourney} className="bg-wt-gold hover:bg-wt-gold-dark text-black">
            <Plus className="h-4 w-4 mr-2" />
            Create Journey
          </Button>
        </div>
      ) : (
        <div className="bg-wt-surface border border-wt-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-wt-border bg-wt-paper/50">
                <th className="text-left px-4 py-3 text-xs font-medium text-wt-text-muted">Journey Name</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-wt-text-muted">Made Live at</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-wt-text-muted">Channels</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-wt-text-muted">Total Revenue</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-wt-text-muted">Recipients</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-wt-text-muted">Open Rate</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-wt-text-muted">Click Rate</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-wt-text-muted">Order Rate</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-wt-text-muted">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJourneys.map((journey) => {
                const trigger = getTriggerInfo(journey.trigger_type);
                const TriggerIcon = trigger.icon;
                
                return (
                  <tr key={journey.id} className="border-b border-wt-border hover:bg-wt-paper/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {getStatusBadge(journey.status)}
                        <span className="text-sm font-medium text-wt-text-primary">{journey.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-wt-text-muted">
                      {journey.activated_at ? new Date(journey.activated_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {journey.nodes?.some(n => n.type === 'send_whatsapp') && (
                          <div className="p-1 rounded bg-green-500/20" title="WhatsApp">
                            <MessageCircle className="h-3 w-3 text-green-400" />
                          </div>
                        )}
                        {journey.nodes?.some(n => n.type === 'send_email') && (
                          <div className="p-1 rounded bg-blue-500/20" title="Email">
                            <Mail className="h-3 w-3 text-blue-400" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-wt-text-primary text-right">
                      {formatCurrency(journey.total_revenue || 0)}
                    </td>
                    <td className="px-4 py-3 text-sm text-wt-text-primary text-right">
                      {journey.total_entered || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-wt-text-primary text-right">
                      {(journey.open_rate || 0).toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-sm text-wt-text-primary text-right">
                      {(journey.click_rate || 0).toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-sm text-wt-text-primary text-right">
                      {(journey.order_rate || 0).toFixed(1)}%
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onViewAnalytics(journey)}
                          className="text-wt-text-muted hover:text-blue-400 h-8 w-8 p-0"
                          title="Analytics"
                          data-testid={`analytics-btn-${journey.id}`}
                        >
                          <BarChart3 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onEditJourney(journey)}
                          className="text-wt-text-muted hover:text-wt-gold h-8 w-8 p-0"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDuplicateJourney(journey)}
                          className="text-wt-text-muted hover:text-wt-text-primary h-8 w-8 p-0"
                          title="Duplicate"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {journey.status === 'active' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onToggleStatus(journey, 'pause')}
                            className="text-yellow-400 hover:text-yellow-300 h-8 w-8 p-0"
                            title="Pause"
                          >
                            <Pause className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onToggleStatus(journey, 'activate')}
                            className="text-green-400 hover:text-green-300 h-8 w-8 p-0"
                            title="Activate"
                            disabled={!journey.nodes?.length}
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDeleteJourney(journey)}
                          className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


// =============================================================================
// JOURNEY BUILDER (Full Page)
// =============================================================================

const JourneyBuilder = ({ journey, templates, onSave, onBack }) => {
  const { formatCurrency } = useSettings();
  // Journey state
  const [name, setName] = useState(journey?.name || 'Untitled Journey');
  const [description, setDescription] = useState(journey?.description || '');
  const [entryTrigger, setEntryTrigger] = useState(journey?.trigger_type || null);
  const [entryConditions, setEntryConditions] = useState(journey?.entry_conditions || []);
  const [exitTriggers, setExitTriggers] = useState(journey?.exit_triggers || []);
  const [nodes, setNodes] = useState(journey?.nodes || []);
  const [showAnalytics, setShowAnalytics] = useState(false);
  
  // UI state
  const [activePanel, setActivePanel] = useState(null); // 'trigger', 'node-{id}', null
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  
  // Analytics data
  const [analytics, setAnalytics] = useState(null);
  
  // Get selected node
  const selectedNode = useMemo(() => {
    return nodes.find(n => n.id === selectedNodeId);
  }, [nodes, selectedNodeId]);
  
  // Add node
  const addNode = (type) => {
    const newNode = {
      id: `node_${Date.now()}`,
      type,
      position: { x: 0, y: nodes.length * 120 },
      next_node_id: null,
      // Analytics
      sent: 0,
      delivered: 0,
      read: 0,
      clicked: 0,
      failed: 0,
      revenue: 0
    };
    
    // Set defaults
    if (type === 'delay') {
      newNode.delay_value = 1;
      newNode.delay_unit = 'hours';
    } else if (type === 'send_whatsapp') {
      newNode.template_name = '';
      newNode.template_params = [];
      newNode.variable_mappings = []; // { variable: '{{1}}', field: 'customer.first_name', fallback: 'there' }
      newNode.is_marketing = true;
    } else if (type === 'condition') {
      newNode.condition_field = 'order_total';
      newNode.condition_operator = 'greater_than';
      newNode.condition_value = 0;
    } else if (type === 'split') {
      newNode.split_percentage = 50;
    } else if (type === 'wait_for_response') {
      newNode.expected_response_type = 'location'; // text, location, image, any
      newNode.timeout_hours = 24;
      newNode.responses_received = 0;
      newNode.timeouts = 0;
    } else if (type === 'update_data') {
      newNode.update_target = 'order'; // order, customer, lead
      newNode.update_fields = []; // [{ field: 'delivery_location.lat', value: '{{response.lat}}' }]
    }
    
    // Link previous node
    if (nodes.length > 0) {
      const updatedNodes = [...nodes];
      updatedNodes[updatedNodes.length - 1].next_node_id = newNode.id;
      setNodes([...updatedNodes, newNode]);
    } else {
      setNodes([newNode]);
    }
    
    setSelectedNodeId(newNode.id);
    setActivePanel(`node-${newNode.id}`);
  };
  
  // Update node
  const updateNode = (nodeId, updates) => {
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, ...updates } : n));
  };
  
  // Remove node
  const removeNode = (nodeId) => {
    const nodeIndex = nodes.findIndex(n => n.id === nodeId);
    if (nodeIndex === -1) return;
    
    const updatedNodes = nodes.filter(n => n.id !== nodeId);
    
    // Fix links
    if (nodeIndex > 0 && updatedNodes[nodeIndex - 1]) {
      updatedNodes[nodeIndex - 1].next_node_id = nodes[nodeIndex].next_node_id;
    }
    
    setNodes(updatedNodes);
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null);
      setActivePanel(null);
    }
  };
  
  // Save journey
  const handleSave = async (publish = false) => {
    if (!name.trim()) {
      toast.error('Please enter a journey name');
      return;
    }
    if (!entryTrigger) {
      toast.error('Please select an entry trigger');
      return;
    }
    
    setSaving(true);
    try {
      await onSave({
        id: journey?.id,
        name,
        description,
        trigger_type: entryTrigger,
        entry_conditions: entryConditions,
        exit_triggers: exitTriggers,
        nodes,
        start_node_id: nodes.length > 0 ? nodes[0].id : null,
        status: publish ? 'active' : journey?.status || 'draft'
      });
    } catch (error) {
      toast.error('Failed to save journey');
    } finally {
      setSaving(false);
    }
  };
  
  // Load analytics
  const loadAnalytics = async () => {
    if (!journey?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/v1/journeys/${journey.id}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics');
    }
  };
  
  useEffect(() => {
    if (showAnalytics && journey?.id) {
      loadAnalytics();
    }
  }, [showAnalytics, journey?.id]);
  
  // Get trigger info
  const entryTriggerInfo = ENTRY_TRIGGERS.find(t => t.value === entryTrigger);
  const EntryIcon = entryTriggerInfo?.icon || Zap;
  
  return (
    <div className="h-screen flex flex-col bg-wt-bg">
      {/* Top Bar */}
      <div className="h-14 border-b border-wt-border bg-wt-surface flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-wt-text-muted hover:text-wt-text-primary"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          
          {isEditingName ? (
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => e.key === 'Enter' && setIsEditingName(false)}
              className="w-64 h-8 bg-wt-paper border-wt-border text-wt-text-primary"
              autoFocus
            />
          ) : (
            <button
              onClick={() => setIsEditingName(true)}
              className="flex items-center gap-2 text-lg font-semibold text-wt-text-primary hover:text-wt-gold"
            >
              {name}
              <Pencil className="h-4 w-4 text-wt-text-muted" />
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Toolbar buttons */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {}}
            className="border-wt-border text-wt-text-muted"
          >
            Pause Retry
          </Button>
          <Button
            variant={showAnalytics ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowAnalytics(!showAnalytics)}
            className={showAnalytics ? 'bg-wt-gold text-black' : 'border-wt-border text-wt-text-muted'}
          >
            <BarChart3 className="h-4 w-4 mr-1" />
            {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {}}
            className="border-wt-border text-wt-text-muted"
          >
            <Settings className="h-4 w-4 mr-1" />
            Spam Control
          </Button>
          
          <div className="w-px h-6 bg-wt-border mx-2" />
          
          <Button
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving}
            className="border-wt-border text-wt-text-muted"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            Save Draft
          </Button>
          <Button
            onClick={() => handleSave(true)}
            disabled={saving || nodes.length === 0}
            className="bg-wt-gold hover:bg-wt-gold-dark text-black"
          >
            <Play className="h-4 w-4 mr-1" />
            Publish Now
          </Button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Analytics Panel (when shown) */}
        {showAnalytics && (
          <div className="w-48 border-r border-wt-border bg-wt-surface p-4 space-y-4 overflow-y-auto">
            <div className="flex items-center justify-between">
              <span className="text-xs text-wt-text-muted">Showing Analytics for</span>
            </div>
            <Select defaultValue="30">
              <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wt-surface border-wt-border">
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="space-y-3">
              <div className="p-3 bg-wt-paper rounded-lg">
                <p className="text-2xl font-bold text-wt-text-primary">{analytics?.totals?.entered || journey?.total_entered || 0}</p>
                <p className="text-xs text-wt-text-muted">Unique Recipients</p>
              </div>
              <div className="p-3 bg-wt-paper rounded-lg">
                <p className="text-2xl font-bold text-wt-text-primary">{(analytics?.open_rate || 0).toFixed(2)}%</p>
                <p className="text-xs text-wt-text-muted">Open Rate</p>
              </div>
              <div className="p-3 bg-wt-paper rounded-lg">
                <p className="text-2xl font-bold text-wt-text-primary">{(analytics?.click_rate || 0).toFixed(2)}%</p>
                <p className="text-xs text-wt-text-muted">CTR</p>
              </div>
              <div className="p-3 bg-wt-paper rounded-lg">
                <p className="text-2xl font-bold text-wt-text-primary">{(analytics?.conversion_rate || 0).toFixed(2)}%</p>
                <p className="text-xs text-wt-text-muted">Conversion Rate</p>
              </div>
              <div className="p-3 bg-wt-paper rounded-lg">
                <p className="text-2xl font-bold text-wt-gold">{formatCurrency(analytics?.revenue || 0)}</p>
                <p className="text-xs text-wt-text-muted">Revenue</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Canvas */}
        <div className="flex-1 bg-[#1a1a1f] overflow-auto p-8">
          <div className="max-w-2xl mx-auto space-y-4">
            {/* Entry Trigger Node */}
            <div
              onClick={() => setActivePanel('trigger')}
              className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                activePanel === 'trigger'
                  ? 'border-wt-gold bg-wt-gold/10'
                  : 'border-orange-500/50 bg-wt-surface hover:border-orange-500'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${entryTriggerInfo?.color || 'bg-orange-500'}`}>
                    <EntryIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-orange-400">Flow Trigger</p>
                    <p className="text-xs text-wt-text-muted">
                      {entryTriggerInfo ? `A user will enter this flow when: ${entryTriggerInfo.label.toUpperCase()}` : 'Click to select trigger'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-wt-text-muted" />
              </div>
            </div>
            
            {/* Flow Arrow */}
            {nodes.length > 0 && (
              <div className="flex justify-center">
                <ArrowDown className="h-6 w-6 text-wt-text-muted" />
              </div>
            )}
            
            {/* Nodes */}
            {nodes.map((node, idx) => {
              const config = ACTION_TYPES[node.type] || { label: node.type, icon: Zap, color: 'bg-gray-500' };
              const NodeIcon = config.icon;
              const isSelected = selectedNodeId === node.id;
              
              return (
                <React.Fragment key={node.id}>
                  <div
                    onClick={() => {
                      setSelectedNodeId(node.id);
                      setActivePanel(`node-${node.id}`);
                    }}
                    className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      isSelected
                        ? 'border-wt-gold bg-wt-gold/10'
                        : `border-wt-border bg-wt-surface hover:border-wt-gold/50`
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${config.color}`}>
                          <NodeIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${config.color.replace('bg-', 'text-').replace('-500', '-400')}`}>
                            {config.label}
                          </p>
                          <p className="text-xs text-wt-text-muted">
                            {node.type === 'send_whatsapp' && (node.template_name ? `Send ${node.template_name}` : 'Select template')}
                            {node.type === 'delay' && `Wait for ${node.delay_value || 0} ${node.delay_unit || 'hours'}`}
                            {node.type === 'condition' && `If ${node.condition_field}`}
                            {node.type === 'split' && `${node.split_percentage}% / ${100 - node.split_percentage}%`}
                            {node.type === 'wait_for_response' && `Wait for ${node.expected_response_type || 'response'} (${node.timeout_hours || 24}h timeout)`}
                            {node.type === 'update_data' && `Update ${node.update_target || 'order'} (${(node.update_fields || []).length} fields)`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); removeNode(node.id); }}
                          className="h-6 w-6 p-0 text-wt-text-muted hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Node Analytics (when shown) */}
                    {showAnalytics && node.type === 'send_whatsapp' && (
                      <div className="mt-3 pt-3 border-t border-wt-border">
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div>
                            <p className="text-sm font-medium text-wt-text-primary">{node.sent || 0}</p>
                            <p className="text-xs text-wt-text-muted">Sent</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-wt-text-primary">{node.clicked || 0}</p>
                            <p className="text-xs text-wt-text-muted">Clicked</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-wt-gold">{formatCurrency(node.revenue || 0)}</p>
                            <p className="text-xs text-wt-text-muted">Revenue</p>
                          </div>
                        </div>
                        <button className="text-xs text-wt-gold mt-2 hover:underline">See More</button>
                      </div>
                    )}
                  </div>
                  
                  {/* Arrow after each node (except last) */}
                  <div className="flex justify-center">
                    <div className="flex flex-col items-center">
                      {node.type === 'send_whatsapp' && (
                        <span className="text-xs text-wt-text-muted mb-1">ON SEND</span>
                      )}
                      <ArrowDown className="h-6 w-6 text-wt-text-muted" />
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            
            {/* Add Node Button */}
            <div className="flex justify-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-dashed border-wt-border text-wt-text-muted hover:border-wt-gold hover:text-wt-gold"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Action
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-wt-surface border-wt-border w-48">
                  {Object.entries(ACTION_TYPES).map(([key, config]) => (
                    <DropdownMenuItem
                      key={key}
                      onClick={() => !config.disabled && addNode(key)}
                      className={`text-wt-text-primary hover:bg-wt-paper ${config.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={config.disabled}
                    >
                      <config.icon className={`h-4 w-4 mr-2 ${config.color.replace('bg-', 'text-').replace('-500', '-400')}`} />
                      {config.label}
                      {config.badge && (
                        <Badge className="ml-auto text-xs bg-wt-paper text-wt-text-muted">{config.badge}</Badge>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        
        {/* Right Side Panel - Actions / Trigger Config */}
        <div className="w-80 border-l border-wt-border bg-wt-surface overflow-y-auto">
          {activePanel === 'trigger' ? (
            <TriggerConfigPanel
              entryTrigger={entryTrigger}
              setEntryTrigger={setEntryTrigger}
              entryConditions={entryConditions}
              setEntryConditions={setEntryConditions}
              exitTriggers={exitTriggers}
              setExitTriggers={setExitTriggers}
              onClose={() => setActivePanel(null)}
            />
          ) : selectedNode ? (
            <NodeConfigPanel
              node={selectedNode}
              updateNode={(updates) => updateNode(selectedNode.id, updates)}
              templates={templates}
              triggerType={entryTrigger}
              onClose={() => { setSelectedNodeId(null); setActivePanel(null); }}
            />
          ) : (
            <div className="p-4">
              <h3 className="text-lg font-semibold text-wt-text-primary mb-4">ACTIONS</h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(ACTION_TYPES).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => !config.disabled && addNode(key)}
                    disabled={config.disabled}
                    className={`p-4 rounded-xl border border-wt-border text-center hover:border-wt-gold/50 transition-colors ${
                      config.disabled ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <config.icon className={`h-8 w-8 mx-auto mb-2 ${config.color.replace('bg-', 'text-').replace('-500', '-400')}`} />
                    <p className="text-xs text-wt-text-primary">{config.label}</p>
                    {config.badge && (
                      <Badge className="mt-1 text-[10px] bg-orange-500/20 text-orange-400">{config.badge}</Badge>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// =============================================================================
// TRIGGER CONFIG PANEL
// =============================================================================

const TriggerConfigPanel = ({ entryTrigger, setEntryTrigger, entryConditions, setEntryConditions, exitTriggers, setExitTriggers, onClose }) => {
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-wt-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChevronLeft className="h-4 w-4 text-wt-text-muted cursor-pointer" onClick={onClose} />
          <span className="font-medium text-wt-text-primary">Select the trigger to start the journey</span>
        </div>
        <Button size="sm" className="bg-wt-gold text-black" onClick={onClose}>
          Save Settings
        </Button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Entry Trigger */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-wt-text-primary">Entry Trigger</h4>
            <button className="text-xs text-red-400 hover:underline" onClick={() => { setEntryTrigger(null); setEntryConditions([]); }}>
              Clear Entry Trigger
            </button>
          </div>
          <p className="text-xs text-wt-text-muted mb-3">Entry trigger determines the events which must occur for a user to enter the journey.</p>
          
          <div className="space-y-2">
            <Label className="text-wt-text-muted text-sm">WHERE</Label>
            <Select value={entryTrigger || ''} onValueChange={setEntryTrigger}>
              <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary">
                <SelectValue placeholder="Select a condition..." />
              </SelectTrigger>
              <SelectContent className="bg-wt-surface border-wt-border max-h-64">
                {Object.entries(
                  ENTRY_TRIGGERS.reduce((acc, t) => {
                    if (!acc[t.category]) acc[t.category] = [];
                    acc[t.category].push(t);
                    return acc;
                  }, {})
                ).map(([category, triggers]) => (
                  <React.Fragment key={category}>
                    <div className="px-2 py-1 text-xs text-wt-text-muted font-medium">{category}</div>
                    {triggers.map(trigger => (
                      <SelectItem key={trigger.value} value={trigger.value} className="text-wt-text-primary">
                        <div className="flex items-center gap-2">
                          <trigger.icon className="h-4 w-4" />
                          {trigger.label}
                        </div>
                      </SelectItem>
                    ))}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Add Condition Button */}
          <Button
            variant="outline"
            size="sm"
            className="mt-3 border-dashed border-wt-border text-wt-text-muted"
            onClick={() => setEntryConditions([...entryConditions, { field: '', operator: 'equals', value: '' }])}
          >
            <Filter className="h-4 w-4 mr-2" />
            Add Condition
          </Button>
          
          {/* Entry Conditions */}
          {entryConditions.map((cond, idx) => (
            <div key={idx} className="mt-3 p-3 bg-wt-paper rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-wt-text-muted">Condition {idx + 1}</span>
                <button onClick={() => setEntryConditions(entryConditions.filter((_, i) => i !== idx))} className="text-red-400">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Select
                  value={cond.field}
                  onValueChange={(v) => {
                    const updated = [...entryConditions];
                    updated[idx].field = v;
                    setEntryConditions(updated);
                  }}
                >
                  <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary text-sm h-8">
                    <SelectValue placeholder="Field" />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    <SelectItem value="order_total">Order Total</SelectItem>
                    <SelectItem value="customer_type">Customer Type</SelectItem>
                    <SelectItem value="items_count">Items Count</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={cond.operator}
                  onValueChange={(v) => {
                    const updated = [...entryConditions];
                    updated[idx].operator = v;
                    setEntryConditions(updated);
                  }}
                >
                  <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary text-sm h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    <SelectItem value="equals">Equals</SelectItem>
                    <SelectItem value="not_equals">Not Equals</SelectItem>
                    <SelectItem value="greater_than">Greater Than</SelectItem>
                    <SelectItem value="less_than">Less Than</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={cond.value}
                  onChange={(e) => {
                    const updated = [...entryConditions];
                    updated[idx].value = e.target.value;
                    setEntryConditions(updated);
                  }}
                  placeholder="Value"
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm h-8"
                />
              </div>
            </div>
          ))}
        </div>
        
        {/* Exit Trigger */}
        <div className="pt-6 border-t border-wt-border">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-wt-text-primary">Exit Trigger</h4>
            <button className="text-xs text-red-400 hover:underline" onClick={() => setExitTriggers([])}>
              Clear Exit Trigger
            </button>
          </div>
          <p className="text-xs text-wt-text-muted mb-3">Exit trigger determines the events which must occur for a user to exit the journey.</p>
          
          <div className="space-y-2">
            <Label className="text-wt-text-muted text-sm">WHERE</Label>
            <Select
              value={exitTriggers[0]?.event || ''}
              onValueChange={(v) => setExitTriggers(v ? [{ event: v }] : [])}
            >
              <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary">
                <SelectValue placeholder="Select a condition..." />
              </SelectTrigger>
              <SelectContent className="bg-wt-surface border-wt-border max-h-64">
                {EXIT_TRIGGERS.map(trigger => (
                  <SelectItem key={trigger.value} value={trigger.value} className="text-wt-text-primary">
                    <div className="flex items-center gap-2">
                      <trigger.icon className="h-4 w-4" />
                      {trigger.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            className="mt-3 border-dashed border-wt-border text-wt-text-muted"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Condition
          </Button>
        </div>
      </div>
    </div>
  );
};


// =============================================================================
// NODE CONFIG PANEL (WhatsApp, Delay, etc.)
// =============================================================================

const NodeConfigPanel = ({ node, updateNode, templates, triggerType, onClose }) => {
  const config = ACTION_TYPES[node.type] || { label: node.type, icon: Zap, color: 'bg-gray-500' };
  // Only offer dynamic product/cart image sources for triggers whose
  // payload actually contains that data (prevents users from picking
  // "Product Image" on a customer_created journey — which would fail
  // at send-time with "no image URL available").
  const availableMediaSources = useMemo(
    () => headerMediaSourcesForTrigger(triggerType),
    [triggerType]
  );
  
  // Get template info for selected template
  const selectedTemplate = templates.find(t => t.name === node.template_name);
  
  // Extract variables from template body
  const templateVariables = useMemo(() => {
    if (!selectedTemplate) return [];
    const body = selectedTemplate.components?.find(c => c.type === 'BODY')?.text || '';
    const matches = body.match(/\{\{(\d+)\}\}/g) || [];
    return [...new Set(matches)];
  }, [selectedTemplate]);
  
  // Initialize variable mappings when template changes
  useEffect(() => {
    if (templateVariables.length > 0 && (!node.variable_mappings || node.variable_mappings.length !== templateVariables.length)) {
      updateNode({
        variable_mappings: templateVariables.map(v => ({
          variable: v,
          field: '',
          fallback: ''
        }))
      });
    }
  }, [templateVariables]);
  
  // Get template preview with variable substitutions
  const getPreviewText = () => {
    if (!selectedTemplate) return '';
    let text = selectedTemplate.components?.find(c => c.type === 'BODY')?.text || '';
    
    (node.variable_mappings || []).forEach((mapping) => {
      const displayValue = mapping.field 
        ? `{{${mapping.field.split('.').pop()}}}`
        : mapping.fallback || mapping.variable;
      text = text.replace(mapping.variable, displayValue);
    });
    
    return text;
  };
  
  if (node.type === 'send_whatsapp') {
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-wt-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChevronLeft className="h-4 w-4 text-wt-text-muted cursor-pointer" onClick={onClose} />
            <span className="font-medium text-wt-text-primary">Actions: Send WhatsApp</span>
          </div>
          <Button size="sm" className="bg-wt-gold text-black" onClick={onClose}>
            Save Changes
          </Button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Marketing Message Checkbox */}
          <label className="flex items-start gap-3 p-3 bg-wt-paper rounded-lg cursor-pointer">
            <Checkbox
              checked={node.is_marketing}
              onCheckedChange={(checked) => updateNode({ is_marketing: checked })}
              className="mt-0.5 border-wt-border data-[state=checked]:bg-wt-gold data-[state=checked]:border-wt-gold"
            />
            <div>
              <p className="text-sm font-medium text-wt-text-primary">Mark this as a Marketing message</p>
              <p className="text-xs text-wt-text-muted">Only marketing messages will be used for attributing revenue.</p>
            </div>
          </label>
          
          {/* Template Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-wt-text-primary">Preview</Label>
              {selectedTemplate && (
                <Badge className={`text-xs ${selectedTemplate.status === 'APPROVED' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                  {selectedTemplate.status}
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-2 mb-3">
              <Select value={node.template_name || ''} onValueChange={(v) => updateNode({ template_name: v })}>
                <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  {templates.filter(t => t.status === 'APPROVED').map(t => (
                    <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedTemplate && (
              <div className="flex items-center gap-2 text-sm">
                <button className="text-wt-gold hover:underline">Edit Variables</button>
                <button className="text-wt-gold hover:underline" onClick={() => updateNode({ template_name: '' })}>Change Template</button>
                <Button variant="outline" size="sm" className="ml-auto border-wt-border text-wt-text-muted">
                  Test
                </Button>
              </div>
            )}
          </div>
          
          {/* Variable Mappings */}
          {templateVariables.length > 0 && (
            <div className="space-y-3">
              <Label className="text-wt-text-primary">Variable Mappings</Label>
              <p className="text-xs text-wt-text-muted">Map template variables to customer/order data fields</p>
              
              {(node.variable_mappings || []).map((mapping, idx) => (
                <div key={idx} className="p-3 bg-wt-paper rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-wt-gold">{mapping.variable}</span>
                  </div>
                  <div>
                    <Label className="text-xs text-wt-text-muted">Data Field</Label>
                    <Select
                      value={mapping.field || ''}
                      onValueChange={(v) => {
                        const updated = [...(node.variable_mappings || [])];
                        updated[idx].field = v;
                        updateNode({ variable_mappings: updated });
                      }}
                    >
                      <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                        <SelectValue placeholder="Select field" />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-surface border-wt-border max-h-64">
                        {Object.entries(VARIABLE_FIELDS).map(([group, fields]) => (
                          <React.Fragment key={group}>
                            <div className="px-2 py-1 text-xs text-wt-text-muted font-medium capitalize">{group}</div>
                            {fields.map(field => (
                              <SelectItem key={field.value} value={field.value} className="text-wt-text-primary">
                                {field.label}
                              </SelectItem>
                            ))}
                          </React.Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-wt-text-muted">Fallback Value</Label>
                    <Input
                      value={mapping.fallback || ''}
                      onChange={(e) => {
                        const updated = [...(node.variable_mappings || [])];
                        updated[idx].fallback = e.target.value;
                        updateNode({ variable_mappings: updated });
                      }}
                      placeholder="e.g., there, Customer"
                      className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                    />
                    <p className="text-xs text-wt-text-muted mt-1">Used when data field is empty</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Header Media Source - for templates with IMAGE/VIDEO/DOCUMENT headers */}
          {selectedTemplate && selectedTemplate.components?.some(c => 
            c.type === 'HEADER' && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(c.format)
          ) && (
            <div className="space-y-3 p-4 bg-wt-paper rounded-lg border border-wt-border">
              <div className="flex items-center gap-2">
                <Image className="h-4 w-4 text-wt-gold" />
                <Label className="text-wt-text-primary font-medium">Header Media Source</Label>
              </div>
              <p className="text-xs text-wt-text-muted">
                This template has a {selectedTemplate.components.find(c => c.type === 'HEADER')?.format?.toLowerCase()} header. 
                Choose how to provide the media:
              </p>
              
              <Select
                value={
                  availableMediaSources.find((s) => s.value === node.header_media_source)
                    ? node.header_media_source
                    : 'static'
                }
                onValueChange={(v) => updateNode({ header_media_source: v, header_media_url: '' })}
              >
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Select media source" />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  {availableMediaSources.map(source => (
                    <SelectItem key={source.value} value={source.value} className="text-wt-text-primary">
                      <div>
                        <span className="font-medium">{source.label}</span>
                        <span className="text-wt-text-muted text-xs ml-2">- {source.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableMediaSources.length <= 2 && (
                <p className="text-[11px] text-amber-300/80 -mt-1">
                  This trigger doesn't carry product/cart data, so only Static URL and Custom Field are offered here.
                </p>
              )}
              
              {node.header_media_source === 'static' && (
                <div>
                  <Label className="text-xs text-wt-text-muted">Static Media URL</Label>
                  <Input
                    value={node.header_media_url || ''}
                    onChange={(e) => updateNode({ header_media_url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                  />
                </div>
              )}
              
              {node.header_media_source === 'custom' && (
                <div>
                  <Label className="text-xs text-wt-text-muted">Custom Data Field Path</Label>
                  <Input
                    value={node.header_media_field || ''}
                    onChange={(e) => updateNode({ header_media_field: e.target.value })}
                    placeholder="e.g., order.line_items.0.image_url"
                    className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                  />
                </div>
              )}
              
              {['product.first_item_image', 'product.first_item_variant_image', 'checkout.first_item_image'].includes(node.header_media_source) && (
                <div className="p-3 bg-green-500/10 rounded border border-green-500/30">
                  <p className="text-xs text-green-400">
                    The {node.header_media_source.includes('variant') ? 'variant' : 'product'} image will be automatically included from the trigger data.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Button Data Mapping - for templates with URL buttons */}
          {selectedTemplate && selectedTemplate.components?.some(c => 
            c.type === 'BUTTONS' && c.buttons?.some(b => b.type === 'URL' && b.url?.includes('{{'))
          ) && (
            <div className="space-y-3 p-4 bg-wt-paper rounded-lg border border-wt-border">
              <div className="flex items-center gap-2">
                <Link className="h-4 w-4 text-wt-gold" />
                <Label className="text-wt-text-primary font-medium">Button URL Variables</Label>
              </div>
              <p className="text-xs text-wt-text-muted">
                Configure dynamic URL variables for buttons
              </p>
              
              {selectedTemplate.components
                .filter(c => c.type === 'BUTTONS')
                .flatMap(c => c.buttons || [])
                .filter(b => b.type === 'URL' && b.url?.includes('{{'))
                .map((btn, idx) => (
                  <div key={idx} className="p-3 bg-wt-bg rounded space-y-2">
                    <span className="text-sm font-medium text-wt-text-primary">{btn.text}</span>
                    <div>
                      <Label className="text-xs text-wt-text-muted">URL Variable Value</Label>
                      <Select
                        value={node.button_url_mappings?.[idx]?.field || ''}
                        onValueChange={(v) => {
                          const mappings = [...(node.button_url_mappings || [])];
                          mappings[idx] = { ...mappings[idx], field: v, button_index: idx };
                          updateNode({ button_url_mappings: mappings });
                        }}
                      >
                        <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                          <SelectValue placeholder="Select field" />
                        </SelectTrigger>
                        <SelectContent className="bg-wt-surface border-wt-border max-h-64">
                          <SelectItem value="checkout.recovery_url" className="text-wt-text-primary">
                            Checkout Recovery URL
                          </SelectItem>
                          <SelectItem value="fulfillment.tracking_url" className="text-wt-text-primary">
                            Tracking URL
                          </SelectItem>
                          <SelectItem value="order.view_url" className="text-wt-text-primary">
                            Order View URL
                          </SelectItem>
                          <SelectItem value="shop.url" className="text-wt-text-primary">
                            Shop URL
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
            </div>
          )}
          
          {/* Message Preview */}
          {selectedTemplate && (
            <div>
              <Label className="text-wt-text-primary mb-2 block">Message Preview</Label>
              <div className="bg-[#0b141a] rounded-lg p-4">
                <div className="bg-[#005c4b] rounded-lg p-3 text-white text-sm max-w-[280px]">
                  <p className="whitespace-pre-wrap">{getPreviewText()}</p>
                  {selectedTemplate.components?.find(c => c.type === 'FOOTER') && (
                    <p className="text-white/60 text-xs mt-2">
                      {selectedTemplate.components.find(c => c.type === 'FOOTER').text}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  if (node.type === 'delay') {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b border-wt-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChevronLeft className="h-4 w-4 text-wt-text-muted cursor-pointer" onClick={onClose} />
            <span className="font-medium text-wt-text-primary">Wait For / Till Time</span>
          </div>
          <Button size="sm" className="bg-wt-gold text-black" onClick={onClose}>
            Save Changes
          </Button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <Label className="text-wt-text-primary">Wait Duration</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Input
                type="number"
                min="1"
                value={node.delay_value || 1}
                onChange={(e) => updateNode({ delay_value: parseInt(e.target.value) || 1 })}
                className="bg-wt-paper border-wt-border text-wt-text-primary"
              />
              <Select value={node.delay_unit || 'hours'} onValueChange={(v) => updateNode({ delay_unit: v })}>
                <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                  <SelectItem value="days">Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Wait for Response node configuration
  if (node.type === 'wait_for_response') {
    const TIMEOUT_PRESETS = [
      { value: 1, label: '1 hour' },
      { value: 6, label: '6 hours' },
      { value: 12, label: '12 hours' },
      { value: 24, label: '24 hours' },
      { value: 48, label: '48 hours' },
      { value: 'custom', label: 'Custom' }
    ];
    
    const isCustomTimeout = !TIMEOUT_PRESETS.slice(0, -1).some(p => p.value === node.timeout_hours);
    
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b border-wt-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChevronLeft className="h-4 w-4 text-wt-text-muted cursor-pointer" onClick={onClose} />
            <span className="font-medium text-wt-text-primary">Wait for Customer Response</span>
          </div>
          <Button size="sm" className="bg-wt-gold text-black" onClick={onClose}>
            Save Changes
          </Button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Expected Response Type */}
          <div>
            <Label className="text-wt-text-primary">Expected Response Type</Label>
            <p className="text-xs text-wt-text-muted mb-2">What type of response to wait for</p>
            <Select 
              value={node.expected_response_type || 'location'} 
              onValueChange={(v) => updateNode({ expected_response_type: v })}
            >
              <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wt-surface border-wt-border">
                <SelectItem value="location">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-teal-400" />
                    Location Pin
                  </div>
                </SelectItem>
                <SelectItem value="text">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-blue-400" />
                    Text Message
                  </div>
                </SelectItem>
                <SelectItem value="image">
                  <div className="flex items-center gap-2">
                    <Image className="h-4 w-4 text-purple-400" />
                    Image
                  </div>
                </SelectItem>
                <SelectItem value="any">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Any Response
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Timeout Duration */}
          <div>
            <Label className="text-wt-text-primary">Timeout Duration</Label>
            <p className="text-xs text-wt-text-muted mb-2">How long to wait for response before taking timeout path</p>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {TIMEOUT_PRESETS.slice(0, -1).map(preset => (
                <button
                  key={preset.value}
                  onClick={() => updateNode({ timeout_hours: preset.value })}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                    node.timeout_hours === preset.value 
                      ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50' 
                      : 'bg-wt-paper border border-wt-border text-wt-text-muted hover:border-wt-gold/50'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
              <button
                onClick={() => updateNode({ timeout_hours: 72 })}
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  isCustomTimeout 
                    ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50' 
                    : 'bg-wt-paper border border-wt-border text-wt-text-muted hover:border-wt-gold/50'
                }`}
              >
                Custom
              </button>
            </div>
            
            {isCustomTimeout && (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={node.timeout_hours || 24}
                  onChange={(e) => updateNode({ timeout_hours: parseInt(e.target.value) || 24 })}
                  className="bg-wt-paper border-wt-border text-wt-text-primary w-24"
                />
                <span className="text-wt-text-muted">hours</span>
              </div>
            )}
          </div>
          
          {/* Info about branching */}
          <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-teal-400 mt-0.5" />
              <div className="text-sm text-teal-400">
                <p className="font-medium">Branching Paths</p>
                <p className="text-teal-400/80 text-xs mt-1">
                  This node creates two paths:<br/>
                  • <strong>Response Received</strong> - Customer replied before timeout<br/>
                  • <strong>Timeout</strong> - No response within time limit
                </p>
              </div>
            </div>
          </div>
          
          {/* Available Response Variables */}
          <div>
            <Label className="text-wt-text-primary">Available Variables After Response</Label>
            <div className="mt-2 bg-wt-paper rounded-lg p-3 text-xs font-mono space-y-1">
              <div className="text-wt-text-muted">{'{{response.received}}'} - true/false</div>
              <div className="text-wt-text-muted">{'{{response.type}}'} - text/location/image</div>
              {(node.expected_response_type === 'location' || node.expected_response_type === 'any') && (
                <>
                  <div className="text-teal-400">{'{{response.lat}}'} - Latitude</div>
                  <div className="text-teal-400">{'{{response.lng}}'} - Longitude</div>
                  <div className="text-teal-400">{'{{response.location_name}}'}</div>
                </>
              )}
              {(node.expected_response_type === 'text' || node.expected_response_type === 'any') && (
                <div className="text-blue-400">{'{{response.text}}'} - Message content</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Update Data node configuration
  if (node.type === 'update_data') {
    const addUpdateField = () => {
      const updated = [...(node.update_fields || []), { field: '', value: '' }];
      updateNode({ update_fields: updated });
    };
    
    const removeUpdateField = (idx) => {
      const updated = (node.update_fields || []).filter((_, i) => i !== idx);
      updateNode({ update_fields: updated });
    };
    
    const updateField = (idx, key, value) => {
      const updated = [...(node.update_fields || [])];
      updated[idx] = { ...updated[idx], [key]: value };
      updateNode({ update_fields: updated });
    };
    
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b border-wt-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChevronLeft className="h-4 w-4 text-wt-text-muted cursor-pointer" onClick={onClose} />
            <span className="font-medium text-wt-text-primary">Update Data</span>
          </div>
          <Button size="sm" className="bg-wt-gold text-black" onClick={onClose}>
            Save Changes
          </Button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Target Entity */}
          <div>
            <Label className="text-wt-text-primary">Update Target</Label>
            <p className="text-xs text-wt-text-muted mb-2">Which entity to update</p>
            <Select 
              value={node.update_target || 'order'} 
              onValueChange={(v) => updateNode({ update_target: v })}
            >
              <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wt-surface border-wt-border">
                <SelectItem value="order">Order</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="lead">Lead</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Update Fields */}
          <div>
            <Label className="text-wt-text-primary">Fields to Update</Label>
            <p className="text-xs text-wt-text-muted mb-2">Map response data to entity fields</p>
            
            <div className="space-y-3">
              {(node.update_fields || []).map((fieldConfig, idx) => (
                <div key={idx} className="p-3 bg-wt-paper rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-wt-text-muted">Field #{idx + 1}</span>
                    <button 
                      onClick={() => removeUpdateField(idx)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-wt-text-muted">Target Field Path</Label>
                    <Input
                      value={fieldConfig.field || ''}
                      onChange={(e) => updateField(idx, 'field', e.target.value)}
                      placeholder="e.g., delivery_location.lat"
                      className="bg-wt-bg border-wt-border text-wt-text-primary mt-1 text-sm"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs text-wt-text-muted">Value (use variables)</Label>
                    <Input
                      value={fieldConfig.value || ''}
                      onChange={(e) => updateField(idx, 'value', e.target.value)}
                      placeholder="e.g., {{response.lat}}"
                      className="bg-wt-bg border-wt-border text-wt-text-primary mt-1 text-sm font-mono"
                    />
                  </div>
                </div>
              ))}
              
              <Button
                variant="outline"
                size="sm"
                onClick={addUpdateField}
                className="w-full border-dashed border-wt-border text-wt-text-muted hover:border-wt-gold hover:text-wt-gold"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Field
              </Button>
            </div>
          </div>
          
          {/* Quick Templates for Location */}
          <div>
            <Label className="text-wt-text-primary">Quick Templates</Label>
            <p className="text-xs text-wt-text-muted mb-2">Common field mappings</p>
            <div className="grid grid-cols-1 gap-2">
              <button
                onClick={() => updateNode({
                  update_target: 'order',
                  update_fields: [
                    { field: 'delivery_location.lat', value: '{{response.lat}}' },
                    { field: 'delivery_location.lng', value: '{{response.lng}}' },
                    { field: 'delivery_location.source', value: 'whatsapp_pin' }
                  ]
                })}
                className="p-2 text-left bg-indigo-500/10 border border-indigo-500/30 rounded-lg hover:border-indigo-500/50 transition-colors"
              >
                <div className="flex items-center gap-2 text-indigo-400 text-sm font-medium">
                  <MapPin className="h-4 w-4" />
                  Save WhatsApp Location to Order
                </div>
                <p className="text-xs text-wt-text-muted mt-1">
                  Saves lat/lng from customer's shared location
                </p>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Default panel for other node types
  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-wt-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChevronLeft className="h-4 w-4 text-wt-text-muted cursor-pointer" onClick={onClose} />
          <span className="font-medium text-wt-text-primary">{config.label}</span>
        </div>
        <Button size="sm" className="bg-wt-gold text-black" onClick={onClose}>
          Save Changes
        </Button>
      </div>
      <div className="flex-1 p-4">
        <p className="text-wt-text-muted text-sm">Configure {config.label.toLowerCase()} settings here.</p>
      </div>
    </div>
  );
};


// =============================================================================
// MAIN PAGE COMPONENT
// =============================================================================

const MarketingJourneysPage = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useSettings();
  const [view, setView] = useState('list'); // 'list' | 'builder'
  const [journeys, setJourneys] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingJourney, setEditingJourney] = useState(null);
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [analyticsJourney, setAnalyticsJourney] = useState(null);
  const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);
  
  useEffect(() => {
    loadJourneys();
    loadTemplates();
    
    // Listen for template gallery open event
    const handleOpenGallery = () => setShowTemplateGallery(true);
    window.addEventListener('openTemplateGallery', handleOpenGallery);
    return () => window.removeEventListener('openTemplateGallery', handleOpenGallery);
  }, []);
  
  const loadJourneys = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/v1/journeys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJourneys(response.data.journeys || []);
    } catch (error) {
      toast.error('Failed to load journeys');
    } finally {
      setLoading(false);
    }
  };
  
  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.log('Could not load WhatsApp templates');
    }
  };
  
  const handleCreateJourney = () => {
    // Navigate to visual builder for new journey
    navigate('/marketing/journeys/builder/new');
  };
  
  const handleEditJourney = (journey) => {
    // Navigate to visual builder
    navigate(`/marketing/journeys/builder/${journey.id}`);
  };
  
  const handleTemplateSelected = (newJourney) => {
    // Navigate to the newly created journey
    navigate(`/marketing/journeys/builder/${newJourney.id}`);
    setShowTemplateGallery(false);
  };
  
  const handleSaveJourney = async (journeyData) => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    
    if (journeyData.id) {
      await axios.put(`${API_BASE_URL}/v1/journeys/${journeyData.id}`, journeyData, { headers });
      toast.success('Journey updated');
    } else {
      await axios.post(`${API_BASE_URL}/v1/journeys`, journeyData, { headers });
      toast.success('Journey created');
    }
    
    loadJourneys();
    setView('list');
    setEditingJourney(null);
  };
  
  const handleDuplicateJourney = async (journey) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/v1/journeys/${journey.id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Journey duplicated');
      loadJourneys();
    } catch (error) {
      toast.error('Failed to duplicate journey');
    }
  };
  
  const handleDeleteJourney = async (journey) => {
    if (!window.confirm(`Delete "${journey.name}"? This cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/v1/journeys/${journey.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Journey deleted');
      loadJourneys();
    } catch (error) {
      toast.error('Failed to delete journey');
    }
  };
  
  const handleToggleStatus = async (journey, action) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = action === 'activate' ? 'activate' : 'pause';
      await axios.post(`${API_BASE_URL}/v1/journeys/${journey.id}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Journey ${action === 'activate' ? 'activated' : 'paused'}`);
      loadJourneys();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} journey`);
    }
  };
  
  const handleViewAnalytics = (journey) => {
    setAnalyticsJourney(journey);
    setShowAnalyticsDialog(true);
  };
  
  if (view === 'builder') {
    return (
      <JourneyBuilder
        journey={editingJourney}
        templates={templates}
        onSave={handleSaveJourney}
        onBack={() => { setView('list'); setEditingJourney(null); }}
      />
    );
  }
  
  return (
    <>
      <JourneyListDashboard
        journeys={journeys}
        templates={templates}
        loading={loading}
        onCreateJourney={handleCreateJourney}
        onEditJourney={handleEditJourney}
        onDeleteJourney={handleDeleteJourney}
        onDuplicateJourney={handleDuplicateJourney}
        onToggleStatus={handleToggleStatus}
        onViewAnalytics={handleViewAnalytics}
        formatCurrency={formatCurrency}
      />
      
      {/* Template Gallery Modal */}
      <TemplateGalleryModal
        open={showTemplateGallery}
        onClose={() => setShowTemplateGallery(false)}
        onSelectTemplate={handleTemplateSelected}
      />
      
      {/* Journey Analytics Dialog */}
      <JourneyAnalyticsDialog
        open={showAnalyticsDialog}
        onClose={() => { setShowAnalyticsDialog(false); setAnalyticsJourney(null); }}
        journey={analyticsJourney}
      />
    </>
  );
};

// Template Gallery Modal (inline since we already have the component)
const TemplateGalleryModal = ({ open, onClose, onSelectTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    const loadTemplates = async () => {
      if (!open) return;
      
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/v1/journeys/templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTemplates(response.data.templates || []);
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Failed to load templates:', error);
        toast.error('Failed to load journey templates');
      } finally {
        setLoading(false);
      }
    };
    
    loadTemplates();
  }, [open]);

  const filteredTemplates = templates.filter(t => {
    if (selectedCategory !== 'all' && t.category !== selectedCategory) return false;
    if (searchQuery && !t.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate) return;
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/v1/journeys/from-template/${selectedTemplate.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Journey created from template!');
      onSelectTemplate(response.data.journey);
      onClose();
    } catch (error) {
      console.error('Failed to create journey:', error);
      toast.error('Failed to create journey from template');
    } finally {
      setCreating(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'recovery': return ShoppingCart;
      case 'engagement': return UserPlus;
      case 'transactional': return Package;
      default: return Zap;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-wt-surface border-wt-border max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-4 border-b border-wt-border">
          <DialogTitle className="text-wt-text-primary text-xl">
            Start with a Template
          </DialogTitle>
          <p className="text-sm text-wt-text-muted mt-1">
            Choose a pre-built journey template to get started quickly
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Left Sidebar */}
            <div className="w-48 border-r border-wt-border pr-4 py-4 space-y-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-wt-gold text-black font-medium'
                    : 'text-wt-text-muted hover:bg-wt-paper hover:text-wt-text-primary'
                }`}
              >
                All Templates
              </button>
              
              {categories.map(cat => {
                const Icon = getCategoryIcon(cat.id);
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      selectedCategory === cat.id
                        ? 'bg-wt-gold text-black font-medium'
                        : 'text-wt-text-muted hover:bg-wt-paper hover:text-wt-text-primary'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {cat.name}
                  </button>
                );
              })}
            </div>

            {/* Right Content */}
            <div className="flex-1 pl-4 py-4 overflow-y-auto">
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-wt-bg border-wt-border text-wt-text-primary mb-4"
              />

              <div className="grid grid-cols-2 gap-4">
                {filteredTemplates.map(template => {
                  const Icon = getCategoryIcon(template.category);
                  const isSelected = selectedTemplate?.id === template.id;
                  const nodeCount = template.flow_data?.nodes?.length || 0;

                  return (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-wt-gold bg-wt-gold/10'
                          : 'border-wt-border hover:border-wt-gold/50 bg-wt-paper'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2 p-1 rounded-full bg-wt-gold">
                          <CheckCircle className="h-3 w-3 text-black" />
                        </div>
                      )}

                      <Badge className="bg-wt-gold/20 text-wt-gold border-wt-gold/30 border mb-3">
                        <Icon className="h-3 w-3 mr-1" />
                        {template.category}
                      </Badge>

                      <h3 className="font-semibold text-wt-text-primary mb-1">
                        {template.name}
                      </h3>

                      <p className="text-xs text-wt-text-muted mb-3 line-clamp-2">
                        {template.description}
                      </p>

                      <div className="flex items-center gap-2 text-xs text-wt-text-muted">
                        <span>{nodeCount} steps</span>
                        <span>•</span>
                        <span className="capitalize">{template.trigger_type?.replace(/_/g, ' ')}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredTemplates.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-wt-text-muted">
                  <Zap className="h-12 w-12 mb-3 opacity-30" />
                  <p className="text-sm">No templates found</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="pt-4 border-t border-wt-border flex items-center justify-between">
          <p className="text-sm text-wt-text-muted">
            {selectedTemplate ? (
              <>Selected: <span className="text-wt-gold">{selectedTemplate.name}</span></>
            ) : (
              'Select a template to continue'
            )}
          </p>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-wt-border text-wt-text-muted"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFromTemplate}
              disabled={!selectedTemplate || creating}
              className="bg-wt-gold text-black hover:bg-wt-gold/90"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Use This Template'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MarketingJourneysPage;
